import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import type { History } from 'history';
import Cookies from 'js-cookie';
import decodeJwt from 'jwt-decode';
import { stringify } from 'qs';

import {
  LogicError,
  UnauthorizedError,
  UnprocessableEntityError,
} from './http-client.errors';
import { updatePath } from './http-client.helpers';
import {
  AccessToken,
  Body,
  CodeAuthorization,
  Config,
  DeleteOptions,
  GetOptions,
  Introspect,
  Method,
  Params,
  PatchOptions,
  PostOptions,
  PutOptions,
  Query,
  RefreshToken,
  RequestDetails,
  RequestOptions,
  Result,
} from './http-client.types';

type UpdateTokensByAuthorizationCodeQuery = {
  readonly grant_type: 'authorization_code';
  readonly code: string;
  readonly redirect_uri: string;
};

type UpdateTokensByRefreshTokenQuery = {
  readonly grant_type: 'refresh_token';
  readonly refresh_token: string;
  readonly username: string;
  readonly account_id: string;
};

type UpdateTokensQuery =
  | UpdateTokensByAuthorizationCodeQuery
  | UpdateTokensByRefreshTokenQuery;

type UpdateTokensPairResult = {
  readonly access_token: string;
  readonly refresh_token: string;
};

export class HttpClient {
  /**
   * Имя куки, используемое для сохранения состояния авторизации
   * Для более подробной информации смотри https://www.rfc-editor.org/rfc/rfc6749.html#section-4.1.1
   */
  public static AUTHORIZATION_STATE_COOKIE_NAME = '__PW_AUTHORIZATION_STATE';

  /**
   * Имя куки, используемое для сохранения адреса страницы перед авторизацией
   */
  public static URL_BEFORE_AUTHORIZATION_COOKIE_NAME = '__PW_URL_BEFORE_AUTHORIZATION';

  /**
   * Экземпляр Axios
   */
  private readonly instance: AxiosInstance;

  /**
   * API для работы с навигацией
   */
  private readonly history: History;

  /**
   * Конфигурация Http Client
   */
  private readonly config: Config;

  /**
   * Токен доступа, используемый для доступа к защищенным ресурсам
   * Для более подробной информации смотри https://www.rfc-editor.org/rfc/rfc6749.html#section-1.4
   */
  private accessToken: AccessToken | null;

  /**
   * Токен обновления, используемый для обновления токена доступа.
   * Для более подробной информации смотри https://www.rfc-editor.org/rfc/rfc6749.html#section-1.5
   */
  private refreshToken: RefreshToken | null;

  /**
   * Происходит ли сейчас обновление токенов
   */
  private isRefreshing: boolean;

  /**
   * Email или username залогиненного пользователя.
   * Берется из jwt (claims).
   */
  private userName: string;

  /**
   * ID аккаунта, под которым залогинен пользователь.
   * Берется из jwt (claims).
   */
  private accountID: string;

  /**
   * Проверить наличие авторизации
   *
   * @param details - детали запроса к защищенным ресурсам
   */
  private checkAuthorization = async (details: RequestDetails | null): Promise<void> => {
    // если происходит процесс обновления токенов, ждем 100 мс и пробуем проверить авторизацию заново
    if (this.isRefreshing) {
      await new Promise((resolve) => {
        setTimeout(resolve, 100);
      });

      await this.checkAuthorization(details);
      return;
    }

    // если нет токена доступа, то клиент точно не авторизован
    if (!this.accessToken) {
      throw new UnauthorizedError('Unauthorized!', details);
    }

    // если время жизни токена вышло, нужно попробовать обновить его с помощью токена обновления
    const currentTime = new Date().getTime() / 1000;
    const introspect = decodeJwt<any>(this.accessToken);
    const isTokenExpired = introspect.expired_at < currentTime;
    if (isTokenExpired) {
      await this.updateTokenByRefreshToken(details);
      await this.checkAuthorization(details);
    }
  };

  /**
   * Обновить токен доступа
   * Для более подробной информации смотри:
   * https://www.rfc-editor.org/rfc/rfc6749.html#section-4.1.3
   * https://www.rfc-editor.org/rfc/rfc6749.html#section-6
   *
   * @param query - параметры запроса, участвующие в обновлении
   */
  private updateToken = async (query: UpdateTokensQuery): Promise<void> => {
    // идентификация клиента происходит с помощью заголовка
    const clientIdInBase64 = btoa(`${this.config.clientId}:`);
    const authorizationHeader = `Basic ${clientIdInBase64}`;

    try {
      this.isRefreshing = true;

      const tokens = await this.post<UpdateTokensPairResult, never, UpdateTokensQuery, never>(
        this.config.urls.token,
        {
          headers: {
            Authorization: authorizationHeader,
          },
          query,
          withAuthorization: false,
          withCredentials: true,
        },
      );

      this.accessToken = tokens.access_token;
      this.refreshToken = tokens.refresh_token;
      this.isRefreshing = false;
    } catch (error) {
      this.accessToken = null;
      this.refreshToken = null;
      this.isRefreshing = false;

      throw error;
    }
  };

  /**
   * Обновить токен доступа с помощью кода авторизации
   * Для более подробной информации смотри: https://www.rfc-editor.org/rfc/rfc6749.html#section-4.1.3
   *
   * @param authorizationCode - код авторизации, полученный от сервера авторизации
   */
  public updateTokenByAuthorizationCode = async (authorizationCode: string): Promise<void> => {
    await this.updateToken({
      grant_type: 'authorization_code',
      code: authorizationCode,
      redirect_uri: `${window.location.origin}${window.location.pathname}`,
    });
  };

  /**
   * Обновить токен доступа с помощью токена обновления
   * Для более подробной информации смотри: https://www.rfc-editor.org/rfc/rfc6749.html#section-6
   *
   * @param details - детали запроса к защищенным ресурсам
   */
  public updateTokenByRefreshToken = async (details: RequestDetails | null): Promise<void> => {
    if (!this.refreshToken) {
      this.accessToken = null;
      throw new UnauthorizedError('Unauthorized!', details);
    }

    await this.updateToken({
      grant_type: 'refresh_token',
      refresh_token: this.refreshToken,
      username: this.userName,
      account_id: this.accountID,
    });
  };

  /**
   * Создать экземпляр класса
   *
   * @param history - API для работы с навигацией
   * @param config - конфигурация Http Client
   */
  public constructor(history: History, config: Config) {
    const { baseURL } = config;
    this.instance = axios.create({ paramsSerializer: stringify, baseURL });

    this.history = history;
    this.config = config;
    this.accessToken = null;
    this.refreshToken = null;
    this.isRefreshing = false;
    this.userName = '';
    this.accountID = '';
  }

  /**
   * Выполнить запрос
   *
   * @param url - адрес запроса
   * @param method - метод запроса
   * @param options - конфигурация запроса
   */
  public request = async <M extends Method, R extends Result, P extends Params | never, Q extends Query | never, B extends Body | never>(url: string, method: M, options: RequestOptions<M, R, P, Q, B>): Promise<R> => {
    const { basicErrorMiddleware, basicResponseMiddleware } = this.config;
    const { errorMiddleware = basicErrorMiddleware, responseMiddleware = basicResponseMiddleware } = options;
    // формируем детали запроса
    const details = {
      url,
      method,
      headers: options.headers,
      params: (options as any).params,
      query: (options as any).query,
      body: (options as any).body,
      withCredentials: options.withCredentials,
      withAuthorization: options.withAuthorization,
    };

    // формируем конфигурацию запроса
    const config: AxiosRequestConfig = {
      url,
      method,
      headers: options.headers,
      validateStatus: null,
      withCredentials: options.withCredentials,
    };

    if ('params' in options) {
      config.url = updatePath(url, options.params);
    }
    if ('query' in options) {
      config.params = options.query;
    }
    if ('body' in options) {
      config.data = options.body;
    }

    if (options.withAuthorization) {
      await this.checkAuthorization(details);
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${this.accessToken}`;
    }

    // запрос может быть выполнен дважды, если произошла ошибка UnauthorizedError,
    // чтобы была возможность автоматически обновить access token с помощью refresh token
    let result;
    try {
      const response = await this.instance.request<R>(config);
      result = await responseMiddleware({
        result: response.data,
        status: response.status,
        message: response.statusText,
        details,
      });
    } catch (firstError) {
      // если ошибка возникшая во время первого запроса не Unauthorized, то прокидываем ее дальше
      if (!(firstError instanceof UnauthorizedError)) {
        await errorMiddleware(firstError);
        // если вдруг в errorMiddleware не кинуто исключение, делаем это за него
        throw firstError;
      }

      // вторая попытка выполнить запрос, но уже с обновлением токенов
      try {
        if (options.withAuthorization) {
          await this.updateTokenByRefreshToken(details);
          await this.checkAuthorization(details);
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${this.accessToken}`;
        }

        const response = await this.instance.request<R>(config);
        result = await responseMiddleware({
          result: response.data,
          status: response.status,
          message: response.statusText,
          details,
        });
      } catch (secondError) {
        await errorMiddleware(secondError);
        // если вдруг в errorMiddleware не кинуто исключение, делаем это за него
        throw secondError;
      }
    }

    return result;
  };

  /**
   * Выполнить GET запрос
   *
   * @param url - адрес запроса
   * @param options - конфигурация GET запроса
   */
  public get = <R extends Result, P extends Params | never, Q extends Query | never>(url: string, options: GetOptions<R, P, Q>): Promise<R> => (
    this.request<Method.GET, R, P, Q, never>(url, Method.GET, options as RequestOptions<Method.GET, R, P, Q, never>)
  );

  /**
   * Выполнить POST запрос
   *
   * @param url - адрес запроса
   * @param options - конфигурация POST запроса
   */
  public post = <R extends Result, P extends Params | never, Q extends Query | never, B extends Body>(url: string, options: PostOptions<R, P, Q, B>): Promise<R> => (
    this.request<Method.POST, R, P, Q, B>(url, Method.POST, options as RequestOptions<Method.POST, R, P, Q, B>)
  );

  /**
   * Выполнить PUT запрос
   *
   * @param url - адрес запроса
   * @param options - конфигурация PUT запроса
   */
  public put = <R extends Result, P extends Params | never, Q extends Query | never, B extends Body>(url: string, options: PutOptions<R, P, Q, B>): Promise<R> => (
    this.request<Method.PUT, R, P, Q, B>(url, Method.PUT, options as RequestOptions<Method.PUT, R, P, Q, B>)
  );

  /**
   * Выполнить PATCH запрос
   *
   * @param url - адрес запроса
   * @param options - конфигурация PATCH запроса
   */
  public patch = <R extends Result, P extends Params | never, Q extends Query | never, B extends Body>(url: string, options: PatchOptions<R, P, Q, B>): Promise<R> => (
    this.request<Method.PATCH, R, P, Q, B>(url, Method.PATCH, options as RequestOptions<Method.PATCH, R, P, Q, B>)
  );

  /**
   * Выполнить DELETE запрос
   *
   * @param url - адрес запроса
   * @param options - конфигурация DELETE запроса
   */
  public delete = <R extends Result, P extends Params | never, Q extends Query | never, B extends Body>(url: string, options: DeleteOptions<R, P, Q, B>): Promise<R> => (
    this.request<Method.DELETE, R, P, Q, B>(url, Method.DELETE, options as RequestOptions<Method.DELETE, R, P, Q, B>)
  );

  /**
   * Выполнить первый шаг авторизации клиента
   * Для более подробной информации смотри https://www.rfc-editor.org/rfc/rfc6749.html#section-4.1.1
   */
  public login = async (): Promise<void> => {
    const newAuthorizationState = Math.random().toString().slice(2, 12);
    Cookies.set(HttpClient.AUTHORIZATION_STATE_COOKIE_NAME, newAuthorizationState);
    Cookies.set(HttpClient.URL_BEFORE_AUTHORIZATION_COOKIE_NAME, window.location.href, this.config.cookie);

    const query = stringify({
      response_type: 'code',
      client_id: this.config.clientId,
      redirect_uri: `${window.location.origin}${window.location.pathname}`,
      state: newAuthorizationState,
    });

    window.location.href = `${this.config.urls.authorize}?${query}`;

    return new Promise(() => {
    });
  };

  /**
   * Выполнить второй шаг авторизации клиента
   * Для более подробной информации смотри https://www.rfc-editor.org/rfc/rfc6749.html#section-4.1.3
   *
   * @param authorization - данные об авторизации клиента на сервере
   */
  public authorize = async <I extends Introspect>(authorization: CodeAuthorization): Promise<I> => {
    const storiedAuthorizationState = Cookies.get(HttpClient.AUTHORIZATION_STATE_COOKIE_NAME);
    const storiedAuthorizationUrl = Cookies.get(HttpClient.URL_BEFORE_AUTHORIZATION_COOKIE_NAME);

    Cookies.remove(HttpClient.AUTHORIZATION_STATE_COOKIE_NAME);
    Cookies.remove(HttpClient.URL_BEFORE_AUTHORIZATION_COOKIE_NAME);

    if (storiedAuthorizationUrl) {
      const url = new URL(storiedAuthorizationUrl);
      this.history.replace({
        pathname: url.pathname,
        search: url.search,
        hash: url.hash,
      });
    }

    if (storiedAuthorizationState !== authorization.state) {
      throw new UnprocessableEntityError('Storied authorization state and received authorization state NOT equals!');
    }

    await this.updateTokenByAuthorizationCode(authorization.code);
    if (!this.accessToken) {
      throw new LogicError('Access token is null after update tokens!');
    }

    const introspect = decodeJwt<I>(this.accessToken);
    this.userName = introspect.username as string;
    this.accountID = (introspect.account_id as number).toString();

    return introspect;
  };

  /**
   * Закончить сеанс
   */
  public logout = async (): Promise<void> => {
    this.accessToken = null;
    this.refreshToken = null;

    await this.get<void, never, never>(
      this.config.urls.logout,
      {
        withAuthorization: false,
        withCredentials: true,
      },
    );
  };

  /**
   * Получить токен доступа
   */
  public getAccessToken = async (): Promise<AccessToken> => {
    await this.checkAuthorization(null);
    if (!this.accessToken) {
      throw new LogicError('Access token is null after check authorization!');
    }

    return this.accessToken;
  };

  /**
   * Установить токен доступа вручную
   *
   * @param accessToken - токен доступа
   */
  public setAccessToken = (accessToken: string): void => {
    this.accessToken = accessToken;
  };
}
